import React, { useState, useEffect } from 'react';
import './Contact.css';

const Contact: React.FC = () => {
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  
  // Get the redirect URL - use production URL if available, otherwise current location
  const getRedirectUrl = () => {
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    if (isLocalhost) {
      return `${window.location.origin}/?submitted=true#contact`;
    }
    return 'https://fsquare.biz/?submitted=true#contact';
  };

  useEffect(() => {
    // Check if user was redirected back after successful submission
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('submitted') === 'true') {
      setShowSuccessMessage(true);
      // Clean up the URL without reloading the page
      const newUrl = window.location.pathname + window.location.hash;
      window.history.replaceState({}, document.title, newUrl);
      
      // Hide success message after 5 seconds
      setTimeout(() => {
        setShowSuccessMessage(false);
      }, 5000);
    }
  }, []);

  const validateForm = (formData: FormData): string[] => {
    const errors: string[] = [];
    
    // Required fields
    const name = formData.get('SingleLine') as string;
    const companyName = formData.get('SingleLine1') as string;
    const contactMethod = formData.get('Radio') as string;
    const email = formData.get('Email') as string;
    const phone = formData.get('PhoneNumber_countrycode') as string;

    // Check required fields
    if (!name?.trim()) {
      errors.push('Name is required');
    }
    
    if (!companyName?.trim()) {
      errors.push('Company Name is required');
    }
    
    if (!contactMethod) {
      errors.push('Please select a preferred contact method');
    }   

    // Conditional validation based on contact method
    if (contactMethod === 'Email' && !email?.trim()) {
      errors.push('Email is required when Email is selected as contact method');
    }
    
    if (contactMethod === 'Phone' && !phone?.trim()) {
      errors.push('Phone number is required when Phone is selected as contact method');
    }

    // Email format validation if provided
    if (email?.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        errors.push('Please enter a valid email address');
      }
    }

    // Phone format validation if provided
    if (phone?.trim()) {
      const phoneRegex = /^[+]?[\d\s-()]{10,}$/;
      if (!phoneRegex.test(phone)) {
        errors.push('Please enter a valid phone number');
      }
    }

    return errors;
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    const form = e.target as HTMLFormElement;
    const formData = new FormData(form);
    
    // Validate form
    const errors = validateForm(formData);
    
    if (errors.length > 0) {
      e.preventDefault(); // Prevent form submission
      setValidationErrors(errors);
      // Scroll to form to show errors
      document.getElementById('form')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    
    // Clear any previous errors
    setValidationErrors([]);
    // Form will submit normally to Zoho
  };

  return (
    <section id="contact" className="contact section-padding">
      <div className="container">
        <div className="section-header text-center mb-8">
          <h2 className="section-title">Get In Touch</h2>
          <p className="section-subtitle">
            Ready to start your project? Contact us for a consultation and quote!
          </p>
        </div>
        
        <div className="contact-content">
          <div className="contact-info">
            <h3>Contact Information</h3>
            <p className="contact-intro">
              We're here to help with all your electrical needs. 
              Reach out to us and we'll respond promptly.
            </p>
            
            <div className="contact-details">
              <div className="contact-item">
                <div className="contact-icon">📞</div>
                <div className="contact-text">
                  <h4>Phone</h4>
                  <a href="tel:+918056177577">+91 80561 77577</a>                  
                </div>
              </div>
              
              <div className="contact-item">
                <div className="contact-icon">✉️</div>
                <div className="contact-text">
                  <h4>Email</h4>
                  <a href="mailto:info@fsquare.biz">info@fsquare.biz</a>
                  {/* <span className="contact-note">Response within 24 hours</span> */}
                </div>
              </div>
              
              {/* <div className="contact-item">
                <div className="contact-icon">📍</div>
                <div className="contact-text">
                  <h4>Service Area</h4>
                  <span>Chennai & Surrounding Areas</span>
                  <span className="contact-note">Licensed & Insured</span>
                </div>
              </div>               */}
              
            </div>
          </div>
          
          <div className="contact-form-container">
            <form 
              action='https://forms.zohopublic.in/habeebmohamedfsq1/form/ClientDetails/formperma/tmLRaVPYnB5OWeprBbVqZVbLbPk44cq-0irsBRPUxWE/htmlRecords/submit' 
              name='form' 
              id='form' 
              method='POST' 
              acceptCharset='UTF-8' 
              encType='multipart/form-data'
              className="contact-form"
              onSubmit={handleSubmit}
            >
              <input type="hidden" name="zf_referrer_name" value="" />
              <input type="hidden" name="zf_redirect_url" value={getRedirectUrl()} />
              <input type="hidden" name="zc_gad" value="" />
              
              <h3>Client Details</h3>
              <p className="form-description">Please fill the details, We will get in touch with you asap</p>
              
              {showSuccessMessage && (
                <div className="form-success">
                  <p>✅ Thank you! Your message has been submitted successfully. We'll get back to you soon.</p>
                </div>
              )}

              {validationErrors.length > 0 && (
                <div className="form-error">
                  <h4>Please fix the following errors:</h4>
                  <ul>
                    {validationErrors.map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="SingleLine">
                    Name <em>*</em>
                  </label>
                  <input 
                    type="text" 
                    name="SingleLine" 
                    id="SingleLine"
                    maxLength={255} 
                    placeholder="Your full name" 
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="SingleLine1">
                    Company Name <em>*</em>
                  </label>
                  <input 
                    type="text" 
                    name="SingleLine1" 
                    id="SingleLine1"
                    maxLength={255} 
                    placeholder="Your company name" 
                  />
                </div>
              </div>

              <div className="form-group">
                <label>
                  Preferred contact method <em>*</em>
                </label>
                <div className="radio-group">
                  <div className="radio-item">
                    <input type="radio" id="Radio_1" name="Radio" value="Email" />
                    <label htmlFor="Radio_1">Email</label>
                  </div>
                  <div className="radio-item">
                    <input type="radio" id="Radio_2" name="Radio" value="Phone" />
                    <label htmlFor="Radio_2">Phone</label>
                  </div>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="Email">Email</label>
                  <input 
                    type="email" 
                    maxLength={255} 
                    name="Email" 
                    id="Email"
                    placeholder="your.email@example.com" 
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="PhoneNumber_countrycode">Phone</label>
                  <input 
                    type="tel" 
                    name="PhoneNumber_countrycode" 
                    id="PhoneNumber_countrycode"
                    maxLength={20} 
                    placeholder="+91 12345 67890" 
                  />
                </div>
              </div>

              {/* <div className="form-group">
                <label>
                  Company address <em>*</em>
                </label>
                <div className="address-group">
                  <div className="form-subgroup">
                    <input 
                      type="text" 
                      maxLength={255} 
                      name="Address_AddressLine1" 
                      placeholder="Street Address"
                    />
                  </div>
                  <div className="form-subgroup">
                    <input 
                      type="text" 
                      maxLength={255} 
                      name="Address_AddressLine2" 
                      placeholder="Address Line 2"
                    />
                  </div>
                  <div className="form-row">
                    <div className="form-subgroup">
                      <input 
                        type="text" 
                        maxLength={255} 
                        name="Address_City" 
                        placeholder="City"
                      />
                    </div>
                    <div className="form-subgroup">
                      <input 
                        type="text" 
                        maxLength={255} 
                        name="Address_Region" 
                        placeholder="Chennai"
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-subgroup">
                      <input 
                        type="text" 
                        maxLength={255} 
                        name="Address_ZipCode" 
                        placeholder="Postal / Zip Code"
                      />
                    </div>
                    <div className="form-subgroup">
                      <select name="Address_Country" defaultValue="India">
                        <option value="-Select-">-Select-</option>
                        <option value="India">India</option>
                        <option value="United States">United States</option>
                        <option value="United Kingdom">United Kingdom</option>
                        <option value="Canada">Canada</option>
                        <option value="Australia">Australia</option>
                        <option value="Germany">Germany</option>
                        <option value="France">France</option>
                        <option value="Japan">Japan</option>
                        <option value="China">China</option>
                        <option value="Singapore">Singapore</option>
                        <option value="UAE">United Arab Emirates</option>
                        <option value="Saudi Arabia">Saudi Arabia</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div> */}

              <div className="form-group">
                <label htmlFor="MultiLine">Additional Information</label>
                <textarea 
                  name="MultiLine" 
                  id="MultiLine"
                  maxLength={65535} 
                  placeholder="Please add details about service you are looking for, We will get in touch with you soon"
                  rows={5}
                ></textarea>
              </div>

              <button type="submit" className="btn-large btn-primary">
                <em>Submit</em>
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
